import React, { useRef, useState, useEffect } from 'react';
import { Card } from 'react-bootstrap';
import ToggleGroupIcon from '../AnimatedIcons/ToggleGroupIcon/ToggleGroupIcon';

type CollapsibleCardProps = {
  title: string;
  types: any;
  filters: string[];
  activeFilters: string[];
  children: React.ReactNode;
  onFilterChange: (filter: string) => void;
};

const KanbanCollapsible = (props: CollapsibleCardProps) => {
  const {
    title,
    types,
    filters,
    activeFilters,
    children,
    onFilterChange
  } = props;

  const [isOpen, setIsOpen] = useState(true);
  const [maxHeight, setMaxHeight] = useState<string | undefined>(undefined);
  const [wasToggledManually, setWasToggledManually] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const toggleCollapse = () => {
    setIsOpen(prev => !prev);
    setWasToggledManually(true);
  };

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const childrenArray = React.Children.toArray(children);
    const hasContent = childrenArray.length > 0;

    if (!hasContent && isOpen) {
      setIsOpen(false);
      setWasToggledManually(false);
      return;
    }

    if (hasContent && !isOpen && !wasToggledManually) {
      setIsOpen(true);
      return;
    }

    if (isOpen) {
      const newHeight = el.scrollHeight + 1;
      setMaxHeight(prev => {
        if (prev === `${newHeight}px`) return prev;
        requestAnimationFrame(() => setMaxHeight(`${newHeight}px`));
        return `${el.offsetHeight}px`;
      });
    } else {
      const currentHeight = el.scrollHeight + 1;
      setMaxHeight(`${currentHeight}px`);
      requestAnimationFrame(() => setMaxHeight('0px'));
    }
  }, [isOpen, children]);

  useEffect(() => {
    const handleResize = () => {
      const el = contentRef.current;
      if (el && isOpen) {
        const newHeight = el.scrollHeight + 1;
        setMaxHeight(`${newHeight}px`);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  return (
    <div className="kanban-month">
      <Card
        className="kanban-month-header"
        onClick={toggleCollapse}
      >
        <div className='kanban-month-header-title'>
          {title}
        </div>
        <div className='kanban-month-header-icon-container'>
          {filters.map((filter: string, i: number) => (
            <div key={i}
              className={`kanban-month-header-icon ${types[filter].className} ${activeFilters.includes(filter) ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onFilterChange(filter);
              }}
            >
              {types[filter].icon}
            </div>
          ))}
          <div className='kanban-month-header-arrow'>
            <ToggleGroupIcon isOpen={isOpen} size={16} />
          </div>
        </div>
      </Card>
      <div
        ref={contentRef}
        className="kanban-month-body"
        style={{ maxHeight }}
      >
        <div className="kanban-month-body-inner">
          {children}
        </div>
      </div>
    </div>
  );
};

export default KanbanCollapsible;